import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Button,
  MobileStepper,
  Card,
  CardContent,
  useTheme,
  Zoom,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  AccountCircle as AccountIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  SupportAgent as SupportIcon,
  Speed as SpeedIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';

const steps = [
  {
    title: '1. Acceso al Portal',
    icon: <AccountIcon sx={{ fontSize: 48, color: '#38bdf8' }} />,
    description: 'Aprenda a ingresar a su cuenta de manera segura.',
    bullets: [
      'Ingrese utilizando su DNI (o RUC) como usuario.',
      'Su contraseña por defecto le será brindada por su proveedor.',
      'Si no recuerda su contraseña, use la opción "Recuperar ahora" para recibir un enlace de restauración en su correo electrónico.',
      'Puede cambiar su contraseña en cualquier momento desde la sección "Mi Perfil".'
    ],
    color: '#38bdf8'
  },
  {
    title: '2. Facturas y Recibos',
    icon: <ReceiptIcon sx={{ fontSize: 48, color: '#a855f7' }} />,
    description: 'Gestione e inspeccione todos sus comprobantes.',
    bullets: [
      'Visualice sus facturas vencidas, pendientes y pagadas en tiempo real.',
      'Vea el desglose completo del costo del plan de internet y servicios adicionales.',
      'Descargue sus recibos o facturas en formato PDF haciendo clic en el ícono de descarga.',
      'Revise las fechas de vencimiento para mantener su servicio siempre activo.'
    ],
    color: '#a855f7'
  },
  {
    title: '3. Registrar Pagos (OCR)',
    icon: <PaymentIcon sx={{ fontSize: 48, color: '#22c55e' }} />,
    description: 'Reporte sus pagos al instante con lectura inteligente de imágenes.',
    bullets: [
      'Haga clic en "Registrar Pago" e indique qué factura desea pagar.',
      'Suba una captura de pantalla o foto del voucher de su transferencia bancaria.',
      '¡Nuestro lector automático (OCR) extraerá la fecha, hora, monto y código de operación del voucher por usted!',
      'Revise que los datos extraídos sean correctos y confirme. Su pago quedará pendiente de aprobación rápida por caja.'
    ],
    color: '#22c55e'
  },
  {
    title: '4. Soporte Técnico',
    icon: <SupportIcon sx={{ fontSize: 48, color: '#f59e0b' }} />,
    description: 'Reporte averías y contacte a nuestros técnicos directamente.',
    bullets: [
      '¿Tiene problemas con su conexión? Abra un ticket de soporte de forma rápida.',
      'Describa el problema (lentejamiento, corte, etc.) y agregue el nivel de prioridad.',
      'Reciba respuestas del equipo técnico directamente en el portal.',
      'Mantenga una conversación en chat con los operadores hasta que su problema quede 100% resuelto.'
    ],
    color: '#f59e0b'
  },
  {
    title: '5. Consumo y Tráfico',
    icon: <SpeedIcon sx={{ fontSize: 48, color: '#ec4899' }} />,
    description: 'Supervise el estado y consumo de su conexión.',
    bullets: [
      'Vea su velocidad de descarga y subida contratada en su perfil.',
      'Monitoree los gráficos interactivos de consumo diario y mensual en tiempo real.',
      'Verifique si su antena y router Mikrotik están respondiendo correctamente (estado en línea).',
      'Confirme que el tráfico de su red coincide con sus expectativas de navegación.'
    ],
    color: '#ec4899'
  }
];

const HelpTutorialDialog = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    onClose();
  };

  const currentStep = steps[activeStep];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          background: 'rgba(17, 24, 39, 0.95)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.8)',
          color: '#ffffff',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(31, 41, 55, 0.4)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)'
            }}
          >
            <CheckIcon sx={{ fontSize: 18, color: '#ffffff' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontFamily: '"Outfit", "Inter", sans-serif',
              background: 'linear-gradient(90deg, #38bdf8 0%, #c084fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Guía de Uso del Portal
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.4)',
            transition: 'all 0.2s',
            '&:hover': {
              color: '#ffffff',
              bgcolor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mb: 4,
            minHeight: '260px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 96,
              height: 96,
              borderRadius: '30px',
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              border: `1px solid rgba(255, 255, 255, 0.05)`,
              boxShadow: `0 8px 32px -8px ${currentStep.color}33`,
              mb: 3,
              transition: 'all 0.3s ease'
            }}
          >
            {currentStep.icon}
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontFamily: '"Outfit", "Inter", sans-serif',
              color: currentStep.color,
              mb: 1
            }}
          >
            {currentStep.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: 500,
              mb: 3,
              maxWidth: '380px'
            }}
          >
            {currentStep.description}
          </Typography>

          <Divider sx={{ width: '100%', borderColor: 'rgba(255, 255, 255, 0.05)', mb: 3 }} />

          <Box sx={{ width: '100%', textAlign: 'left', px: { xs: 1, sm: 2 } }}>
            {currentStep.bullets.map((bullet, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  mb: 1.5,
                  animation: 'fadeInUp 0.3s ease-out'
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: currentStep.color,
                    mt: 1,
                    flexShrink: 0
                  }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5 }}>
                  {bullet}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            bgcolor: 'transparent',
            p: 0,
            '& .MuiMobileStepper-dot': {
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              mx: 0.75,
              width: 8,
              height: 8,
              transition: 'all 0.3s ease'
            },
            '& .MuiMobileStepper-dotActive': {
              bgcolor: currentStep.color,
              transform: 'scale(1.25)'
            }
          }}
          nextButton={
            activeStep === steps.length - 1 ? (
              <Button
                size="small"
                onClick={handleReset}
                variant="contained"
                sx={{
                  borderRadius: '10px',
                  background: 'linear-gradient(95deg, #22c55e 0%, #16a34a 100%)',
                  fontWeight: 700,
                  color: '#ffffff',
                  px: 3,
                  py: 1,
                  boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(95deg, #16a34a 0%, #15803d 100%)'
                  }
                }}
              >
                Entendido
              </Button>
            ) : (
              <Button
                size="small"
                onClick={handleNext}
                disabled={activeStep === steps.length - 1}
                sx={{
                  color: currentStep.color,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '10px',
                  px: 2,
                  py: 1,
                  '&:hover': {
                    bgcolor: `${currentStep.color}15`
                  }
                }}
                endIcon={<NavigateNextIcon />}
              >
                Siguiente
              </Button>
            )
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                px: 2,
                py: 1,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: '#ffffff'
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.15)'
                }
              }}
              startIcon={<NavigateBeforeIcon />}
            >
              Atrás
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
};

export default HelpTutorialDialog;
